import type {
  AdminVatRule,
  AdminVatRulesImportResult,
  AdminActivationPreview,
  AdminSignupRow,
  SignupSettings,
  AdminCompanyRow,
  AdminCreateCompany,
  AdminCreateCompanyResult,
  AdminLoginRequest,
  AdminLoginResult,
  AdminMe,
  AdminModuleRequest,
  AdminRecoveryLinkResult,
  AdminModeratorCreate,
  AdminModeratorCreated,
  AdminModeratorRow,
  AdminModeratorUpdate,
  AdminBillingPayment,
  AdminBillingRow,
  AdminPaymentClaim,
  AdminPaymentInput,
  AdminAiOverview,
  AdminDashboard,
  AdminInviteCreate,
  AdminInviteResult,
  AdminInviteRow,
  AdminOutletList,
  AdminOutletModule,
  AdminPeriod,
  AiModel,
  AdminPlanSave,
  AdminPlansResponse,
  AdminSubscriptionSet,
  AdminTotpSetup,
  VatRegimeItem,
} from '@kassa/shared';

// Admin paneli: ayrıca sessiya (kassa_admin_sid). Xətanın kodu (məs. totp_required) ekrana lazımdır.
export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { message?: string | string[]; code?: string };
  if (!res.ok) {
    const message = Array.isArray(data.message) ? data.message.join(', ') : data.message;
    throw new AdminApiError(res.status, data.code ?? null, message ?? 'Xəta baş verdi');
  }
  return data as T;
}

export const adminApi = {
  me: () => call<AdminMe>('GET', '/api/admin/me'),
  login: (body: AdminLoginRequest) => call<AdminLoginResult>('POST', '/api/admin/login', body),
  loginTotp: (code: string) => call<AdminMe>('POST', '/api/admin/login/totp', { code }),
  logout: () => call<void>('POST', '/api/admin/logout'),
  totpSetup: () => call<AdminTotpSetup>('POST', '/api/admin/totp/setup'),
  totpEnable: (code: string) => call<AdminMe>('POST', '/api/admin/totp/enable', { code }),
  totpDisable: (code: string) => call<AdminMe>('POST', '/api/admin/totp/disable', { code }),
  companies: () => call<AdminCompanyRow[]>('GET', '/api/admin/companies'),
  plans: () => call<AdminPlansResponse>('GET', '/api/admin/plans'),
  invites: () => call<{ ttlHours: number; items: AdminInviteRow[] }>('GET', '/api/admin/invites'),
  inviteCreate: (body: AdminInviteCreate) => call<AdminInviteResult>('POST', '/api/admin/invites', body),
  inviteResend: (id: string) => call<{ expiresAt: string }>('POST', `/api/admin/invites/${id}/resend`),
  inviteLink: (id: string) => call<{ expiresAt: string; link?: string }>('POST', `/api/admin/invites/${id}/link`),
  inviteRevoke: (id: string) => call<void>('POST', `/api/admin/invites/${id}/revoke`),
  // Öz qeydiyyatı və sınaq (KT-85)
  signupSettings: () => call<SignupSettings>('GET', '/api/admin/signup-settings'),
  signupSettingsSet: (body: SignupSettings) => call<void>('PUT', '/api/admin/signup-settings', body),
  signups: (status: string) => call<AdminSignupRow[]>('GET', `/api/admin/signups${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  signupApprove: (id: string) => call<void>('POST', `/api/admin/signups/${id}/approve`),
  signupReject: (id: string, note: string) => call<void>('POST', `/api/admin/signups/${id}/reject`, { note }),
  dashboard: (period: AdminPeriod) => call<AdminDashboard>('GET', `/api/admin/dashboard?period=${period}`),
  ai: (month: string) => call<AdminAiOverview>('GET', `/api/admin/ai?month=${month}`),
  aiSettings: (model: AiModel, defaultLimitAzn: string) => call<void>('PUT', '/api/admin/ai/settings', { model, defaultLimitAzn }),
  aiTenant: (tenantId: string, enabled: boolean, limitAzn: string | null) => call<void>('PUT', `/api/admin/ai/tenants/${tenantId}`, { enabled, limitAzn }),
  outlets: (query: string) => call<AdminOutletList>('GET', `/api/admin/outlets?${query}`),
  outletModules: (tenantId: string, outletId: string) => call<AdminOutletModule[]>('GET', `/api/admin/outlets/${tenantId}/${outletId}/modules`),
  outletModulesSet: (tenantId: string, outletId: string, modules: Record<string, boolean>) =>
    call<void>('POST', `/api/admin/outlets/${tenantId}/${outletId}/modules`, { modules }),
  outletStatus: (tenantId: string, outletId: string, status: 'frozen' | 'active') =>
    call<void>('POST', `/api/admin/outlets/${tenantId}/${outletId}/status`, { status }),
  billing: (month: string) => call<AdminBillingRow[]>('GET', `/api/admin/billing?month=${encodeURIComponent(month)}`),
  billingRefresh: (month: string) => call<{ rows: number }>('POST', '/api/admin/billing/refresh', { month }),
  billingClose: (month: string) => call<{ rows: number }>('POST', '/api/admin/billing/close', { month }),
  billingPayments: (id: string) => call<AdminBillingPayment[]>('GET', `/api/admin/billing/${id}/payments`),
  billingPay: (id: string, body: AdminPaymentInput) => call<{ id: string }>('POST', `/api/admin/billing/${id}/payments`, body),
  paymentReverse: (id: string, note: string) => call<{ id: string }>('POST', `/api/admin/payments/${id}/reverse`, { note }),
  claims: (status: 'pending' | 'all') => call<AdminPaymentClaim[]>('GET', `/api/admin/payment-claims?status=${status}`),
  claimDecide: (id: string, approve: boolean, note?: string) => call<{ status: string }>('POST', `/api/admin/payment-claims/${id}`, { approve, note }),
  planSave: (code: string, body: AdminPlanSave) => call<{ version: number }>('PUT', `/api/admin/plans/${code}`, body),
  addonPrice: (code: string, priceMonth: string | null) => call<void>('PUT', `/api/admin/addons/${code}`, { priceMonth }),
  subscriptionSet: (tenantId: string, outletId: string, body: AdminSubscriptionSet) =>
    call<void>('PUT', `/api/admin/subscriptions/${tenantId}/${outletId}`, body),
  activationPreview: (tenantId: string, outletId: string) =>
    call<AdminActivationPreview>('GET', `/api/admin/subscriptions/${tenantId}/${outletId}/activation-preview`),
  vatRegimes: () => call<VatRegimeItem[]>('GET', '/api/admin/vat-regimes'),
  createCompany: (body: AdminCreateCompany) => call<AdminCreateCompanyResult>('POST', '/api/admin/companies', body),
  recoveryLink: (accountId: string, phoneVerified: boolean) =>
    call<AdminRecoveryLinkResult>('POST', `/api/admin/accounts/${accountId}/recovery-link`, { phoneVerified }),
  moderators: () => call<AdminModeratorRow[]>('GET', '/api/admin/moderators'),
  createModerator: (body: AdminModeratorCreate) => call<AdminModeratorCreated>('POST', '/api/admin/moderators', body),
  updateModerator: (id: string, body: AdminModeratorUpdate) => call<{ ok: true }>('PATCH', `/api/admin/moderators/${id}`, body),
  setupModerator: (code: string, password: string) => call<void>('POST', '/api/admin/setup', { code, password }),
  moduleRequests: (status: 'new' | 'all') => call<AdminModuleRequest[]>('GET', `/api/admin/module-requests?status=${status}`),
  decide: (tenantId: string, id: string, decision: 'done' | 'rejected') =>
    call<{ status: 'done' | 'rejected' }>('POST', `/api/admin/module-requests/${tenantId}/${id}`, { decision }),
  // ƏDV qaydaları (KT-69): siyahı və CSV (dryRun — yalnız yoxlama)
  vatRules: () => call<AdminVatRule[]>('GET', '/api/admin/vat-rules'),
  vatRulesImport: (csv: string, dryRun: boolean) => call<AdminVatRulesImportResult>('POST', '/api/admin/vat-rules/import', { csv, dryRun }),
};
